import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Button } from 'react-bootstrap';
import moment from 'moment';
import _ from 'lodash';
import Draggable from 'react-draggable';
import Highcharts from 'highcharts';
import Exporting from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting.src';

import withChartExport from '../../../components/ChartExporting';
import { saveConfig, updateLineChart, updateWipBreakdownType } from '../../../components/Filter/FilterActions';
import { getWipBreakdownData } from '../CTAActions';
import dateUtils from '../../../util/dates';
import throttle from '../../../util/throttle';
import { escapeHTML } from '../../../util/text';

import expandIcon from '../../../assets/img/zoom-icon-off.svg';
import collapseIcon from '../../../assets/img/zoom-icon-on.svg';
import pieIcon from '../../../assets/img/pie-icon.svg';
import lineChartIcon from '../../../assets/img/line-chart-icon-on.svg';

import highchartFactory from '../../../util/highcharts';
import ChartDownloadMenu from '../../../components/ChartDownloadMenu';
import chartUtils from '../../../util/chartUtils';
import legendIcon from '../../../assets/img/legend.svg';
import { ViewSwitch, CardList, PieLegend, lightSlice, switchSlice, toggledNames } from '../../../components/CardViews/CardViews';
const highchartUtils = highchartFactory(Highcharts);

const config = {
  chart: {
    type: 'spline',
    backgroundColor: '#fff'
  },
  exporting: {
    enabled: false
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  legend: {
    enabled: false
  },
  colors: ['#0086b3'],
  tooltip: {
    shared: true,
    useHTML: true,
    formatter: function() {}
  },
  xAxis: {
    type: 'datetime',
    gridLineWidth: 0,
    ordinal: false,
    showLastTickLabel: true,
    title: {
      text: null
    },
    plotLines: [
      {
        value: 0
      }
    ]
  },
  yAxis: {
    gridLineWidth: 0,
    labels: {
      enabled: false
    },
    title: {
      text: null
    },
    showFirstLabel: false
  },
  plotOptions: {
    spline: {
      color: '#7CB5EC',
      lineWidth: 1.5,
      marker: {
        enabledThreshold: 2,
        lineWidth: 1,
        radius: 5
      },
      states: {
        hover: {
          lineWidth: 2,
          halo: {
            opacity: 0
          },
          marker: {
            enabled: true
          }
        }
      }
    }
  },
  series: [
    {
      marker: {
        symbol: 'circle'
      },
      name: 'WIP',
      data: []
    }
  ]
};

const pieConfig = {
  chart: {
    backgroundColor: '#fff',
    type: 'pie',
    spacing: [0, 0, 0, 0],
    events: {
      render: function() {
        chartUtils.drawSliceEdges(this);
      }
    }
  },
  credits: {
    enabled: false
  },
  title: {
    text: ''
  },
  exporting: {
    enabled: false
  },
  legend: {
    enabled: false,
    width: 230,
    layout: 'vertical',
    align: 'left',
    verticalAlign: 'top',
    itemMarginTop: 4,
    itemMarginBottom: 4,
    itemStyle: {
      fontSize: '14px',
      fontWeight: 'normal',
      color: 'rgba(0, 0, 0, 0.87)'
    }
  },
  tooltip: {
    useHTML: true,
    outside: true,
    formatter: function() {}
  },
  xAxis: {
    type: 'category'
  },
  plotOptions: {
    pie: {
      center: ['74%', '50%'],
      ...chartUtils.DONUT_OPTIONS,
      allowPointSelect: false,
      cursor: 'pointer',
      point: {
        events: {
          click: function() {}
        }
      },
      dataLabels: {
        enabled: false,
        useHTML: true,
        formatter: function() {},
        style: {
          fontWeight: 'normal',
          fontSize: '12px'
        }
      }
    }
  },
  series: []
};

export class DailyWIP extends React.Component {
  static propTypes = {
    day: PropTypes.number,
    data: PropTypes.array,
    dayData: PropTypes.object,
    dateFormat: PropTypes.string,
    dashboard: PropTypes.object,
    wipBreakdownType: PropTypes.string,
    wipBreakdownBy: PropTypes.string,
    wipBreakdownData: PropTypes.array,
    wipBreakdownLoading: PropTypes.bool,
    onSegmentClick: PropTypes.func,
    chartExporting: PropTypes.object,
    close: PropTypes.func,
    saveConfig: PropTypes.func,
    setWipBreakdownType: PropTypes.func,
    getWipBreakdownData: PropTypes.func
  };

  static defaultProps = {
    day: 0
  };

  constructor(props) {
    super(props);

    this.state = {
      isFullScreen: false,
      width: undefined,
      height: undefined,
      pos: { x: 95, y: 112 },
      currentWip: 0,
      data: [],
      isLabelsVisible: false,
      isListView: !props.wallmode,
      hoveredSlice: null,
      hiddenSlices: []
    };

    this.handleClose = this.handleClose.bind(this);
    this.calculateChartData = throttle(this._calculateChartData, 500, this);
  }

  componentDidMount() {
    highchartUtils.setDefaults();
    Exporting(Highcharts);
    OfflineExporting(Highcharts);

    config.exporting = { ...config.exporting, ...this.props.chartExporting };
    pieConfig.exporting = { ...pieConfig.exporting, ...this.props.chartExporting };

    this.initChart();
    this.updateFontSize();

    const isPieEnabled = this.props.wipBreakdownType === 'pie';
    if (this.props.data && this.props.data.length) {
      if (!isPieEnabled) {
        this.updateDateFormat(this.props.dateFormat);
        this.calculateChartData();
        if (this.state.isListView) {
          this.requestBreakdown();
        }
      } else {
        this.requestBreakdown();
      }
    }
  }

  componentDidUpdate(prevProps) {
    const isPieEnabled = this.props.wipBreakdownType === 'pie';
    const wasPieEnabled = prevProps.wipBreakdownType === 'pie';

    if (wasPieEnabled !== isPieEnabled) {
      this.switchChartType();
    }

    if (!isPieEnabled) {
      if (prevProps.data != this.props.data) {
        this.calculateChartData();
      }

      if (this.state.isListView) {
        this.requestBreakdown();
      }

      if (prevProps.dateFormat != this.props.dateFormat) {
        this.updateDateFormat(this.props.dateFormat);
      }

      if (prevProps.day !== this.props.day) {
        this.updateCurrentWip();
      }
    } else {
      this.requestBreakdown();

      if (prevProps.wipBreakdownData !== this.props.wipBreakdownData) {
        this.updatePieChartData();
        if (this.props.wallmode) {
          this.updateFontSize();
        }
      }
    }
  }

  componentWillUnmount() {
    this.chart.destroy();
  }

  requestBreakdown() {
    const entry = this.props.dayData;
    if (!entry || !(this.props.data || []).includes(entry) ||
      (entry === this.breakdownEntry && this.props.wipBreakdownBy === this.breakdownBy)) {
      return;
    }
    this.breakdownEntry = entry;
    this.breakdownBy = this.props.wipBreakdownBy;
    this.props.getWipBreakdownData([entry], 0, this.props.wipBreakdownBy);
  }

  initChart = () => {
    const isPieChart = this.props.wipBreakdownType === 'pie';
    const chartConfig = isPieChart ? pieConfig : config;
    const that = this;

    if (isPieChart) {
      chartConfig.tooltip.formatter = function() {
        return that.pieTooltipFormatter(this);
      };
      chartConfig.plotOptions.pie.dataLabels.formatter = function() {
        return that.pieDataLabelFormatter(this);
      };
      chartConfig.plotOptions.pie.point.events.click = function() {
        that.onPieSegmentClick(this);
      };
      chartConfig.plotOptions.pie.point.events.mouseOver = function() {
        chartUtils.liftSlice(this, true);
        that.setState({ hoveredSlice: this.name });
      };
      chartConfig.plotOptions.pie.point.events.mouseOut = function() {
        chartUtils.liftSlice(this, false);
        that.setState({ hoveredSlice: null });
      };
    } else {
      chartConfig.tooltip.formatter = function() {
        return that.tooltipFormatter(this, that);
      };
    }

    this.chart = new Highcharts.Chart('daily_wip_chart_container', chartConfig);
  };

  switchChartType = () => {
    this.chart.destroy();
    this.initChart();

    const isPieEnabled = this.props.wipBreakdownType === 'pie';
    if (isPieEnabled && this.props.dayData) {
      if (this.props.wipBreakdownData && this.props.wipBreakdownData.length) {
        this.updatePieChartData();
      }
      this.requestBreakdown();
    } else if (this.props.data && this.props.data.length) {
      this.updateDateFormat(this.props.dateFormat);
      this._calculateChartData();
    }

    this.updateFontSize();
  };

  updatePieChartData = () => {
    if (!this.chart || !this.props.wipBreakdownData) {
      return;
    }

    while (this.chart.series.length > 0) {
      this.chart.series[0].remove(false);
    }

    const breakdownData = this.props.wipBreakdownData;
    const total = _.sumBy(breakdownData, 'y');
    this.setState({ currentWip: total, hiddenSlices: [] });

    this.chart.addSeries({
      legendType: 'point',
      showInLegend: true,
      colorByPoint: true,
      data: chartUtils.donutData(breakdownData)
    }, true);
  };

  pieTooltipFormatter(ev) {
    const name = escapeHTML(ev.key);
    return `<div class="tooltip-label-item tooltip-label-box" style="color: #001A33"><span class="label-box" style="background-color: ${ev.color}";></span><strong>${name}: </strong> (${ev.y} ${ev.y === 1 ? 'item' : 'items'}) ${ev.percentage.toFixed(2)}%</div>`;
  }

  pieDataLabelFormatter(ev) {
    const name = escapeHTML(ev.key);
    const number = ev.y;
    const percentage = ev.percentage.toFixed(2);
    return `<div class="tooltip-label-item tooltip-label-box" style="${chartUtils.pieLabelStyle(ev, 20)}"><strong>${name} (${percentage}%) </strong>${number} ${number === 1 ? 'item' : 'items'}</div>`;
  }

  onPieSegmentClick = (point) => {
    const segmentName = point.name;
    const segmentData = this.props.wipBreakdownData.find(d => d.name === segmentName);

    if (segmentData && segmentData.items && this.props.onSegmentClick) {
      this.props.onSegmentClick({
        name: segmentName,
        count: segmentData.y,
        color: segmentData.color,
        items: segmentData.items
      });
    }
  };

  updateDateFormat(dateFormat) {
    this.chart.update({
      xAxis: {
        dateTimeLabelFormats: dateUtils.createHighchartFormats(dateFormat)
      }
    });
  }

  updateCurrentWip = () => {
    const point = this.state.data[this.props.day];
    if (!point) {
      return false;
    }

    this.setState({
      currentWip: point.y
    });
  };

  _calculateChartData() {
    if (!this.chart || !this.chart.update || !this.chart.options) {
      return false;
    }

    const data = this.generateData(this.props.data);
    if (!data.length) {
      return false;
    }

    this.setState({ data, currentWip: data.length ? _.last(data).y : 0 });

    this.chart.update({
      series: [
        chartUtils.trendLineSeries({ data, color: chartUtils.TREND_LINE_COLOR }, true)
      ],
      xAxis: {
        plotLines: [
          {
            color: '#F5F5F5',
            width: 1,
            value: data[0].x,
            label: {
              text: ''
            }
          }
        ]
      }
    });
  }

  generateData = data => {
    const chartData = _(data)
      .flatMap(i => {
        return _.flatMap(i.series, 'data');
      })
      .sortBy('lastDate')
      .groupBy(i => moment(i.lastDate).startOf('day'))
      .map((i, date) => {
        return {
          x: +moment(date).local(),
          y: i.length
        };
      })
      .value();

    _(data)
      .filter(d => {
        return !d.series.length;
      })
      .forEach(d => {
        chartData.push({
          x: +moment(d.date).local(),
          y: 0
        });
      });

    return _.sortBy(chartData, 'x');
  };

  updateFontSize = () => {
    if (!this.chart) return;

    const isFullscreen = this.state.isFullScreen;
    const { isLabelsVisible } = this.state;
    const isPieChart = this.props.wipBreakdownType === 'pie';

    if (isPieChart) {
      this.chart.update({
        exporting: {
          enabled: isFullscreen,
          sourceWidth: 1000,
          sourceHeight: 600,
          chartOptions: {
            chart: {
              spacingTop: 50,
              spacingRight: 180,
              spacingBottom: 50,
              spacingLeft: 180
            },
            title: {
              text: 'WIP breakdown',
              style: {
                fontSize: '16px',
                fontWeight: 'bold'
              }
            },
            plotOptions: {
              pie: {
                dataLabels: chartUtils.pieExportLabels(point => `${point.y} ${point.y === 1 ? 'item' : 'items'}`)
              }
            }
          }
        },
        legend: {
          enabled: isLabelsVisible,
          ...chartUtils.pieLegend(isFullscreen)
        },
        plotOptions: {
          pie: {
            size: isFullscreen ? '60%' : '75%',
            dataLabels: isFullscreen ? {
              enabled: true,
              useHTML: true,
              connectorWidth: 2,
              distance: 20,
              crop: false,
              overflow: 'justify',
              style: {
                fontSize: '16px',
                fontWeight: 'normal',
                textOutline: 'none',
                whiteSpace: 'normal'
              }
            } : {
              enabled: false,
              style: {
                fontSize: '11px'
              }
            }
          }
        }
      });
    } else {
      const opt = {
        exporting: {
          enabled: isFullscreen,
          chartOptions: {
            title: {
              text: 'WIP',
              style: {
                fontSize: '16px',
                fontWeight: 'bold'
              }
            }
          }
        },
        plotOptions: {
          spline: {
            dataLabels: {
              style: {
                fontSize: isFullscreen ? '16px' : '11px',
                fontWeight: 'bold',
                color: 'contrast'
              }
            }
          }
        }
      };

      if (isFullscreen) {
        opt.yAxis = {
          gridLineWidth: 1,
          labels: {
            enabled: true
          }
        };
      } else {
        opt.yAxis = {
          gridLineWidth: 0,
          labels: {
            enabled: false
          }
        };
      }

      this.chart.update(opt);
    }

    if (isPieChart) {
      this.chart.update({
        legend: { enabled: isFullscreen ? isLabelsVisible : false },
        plotOptions: { pie: { center: isFullscreen ? ['50%', '50%'] : chartUtils.cardPieCenter(this.chart, this.props.wallmode) } }
      });
    }
    chartUtils.placePieNames(this, this.props.wipBreakdownType === 'pie' && !this.state.isFullScreen);

    this.chart.reflow();
  };

  handleToggleChartType = () => {
    const newType = this.props.wipBreakdownType === 'pie' ? 'line' : 'pie';
    this.props.setWipBreakdownType(newType);
    this.props.saveConfig();
  };

  getView() {
    if (this.state.isListView) {
      return 'list';
    }
    return this.props.wipBreakdownType === 'pie' ? 'pie' : 'line';
  }

  handleView = view => {
    if (view === 'list') {
      this.setState({ isListView: true });
      this.requestBreakdown();
      return;
    }
    this.setState({ isListView: false });
    if ((view === 'pie') !== (this.props.wipBreakdownType === 'pie')) {
      this.handleToggleChartType();
    }
  };

  highlightSlice = (name, on) => {
    if (lightSlice(this.chart, name, on) || !on) {
      this.setState({ hoveredSlice: on ? name : null });
    }
  };

  toggleSlice = name => {
    switchSlice(this.chart, name);
    this.setState({ hiddenSlices: toggledNames(this.state.hiddenSlices, name) });
  };

  renderRankedList() {
    const data = this.props.wipBreakdownData || [];
    const rows = chartUtils.rankedShares(data).map(item => ({
      name: item.name,
      color: item.color,
      values: [item.share, `${item.y} ${item.y === 1 ? 'item' : 'items'}`]
    }));
    const waiting = !this.props.wipBreakdownData || (!!this.props.wipBreakdownLoading && !this.props.wipBreakdownData.length);
    return <CardList keyDots columns={['Share', 'Items']} rows={rows} loading={waiting} />;
  }

  handleClose() {
    this.props.close();
    this.props.saveConfig();
  }

  handleDrag = (e, data) => {
    this.setState({ pos: { x: data.x, y: data.y } });
  };

  handleResize = () => {
    if (this.state.isFullScreen) {
      this.setState(
        {
          width: undefined,
          height: undefined,
          isFullScreen: false,
          isLabelsVisible: this.labelsBeforeFocus,
          pos: { x: 95, y: 112 }
        },
        () => this.updateFontSize()
      );
    } else {
      this.labelsBeforeFocus = this.state.isLabelsVisible;
      this.setState(
        {
          width: '100%',
          height: '100%',
          isFullScreen: true,
          isLabelsVisible: true,
          pos: { x: 0, y: 0 }
        },
        () => this.updateFontSize()
      );
    }
  };

  tooltipFormatter(chartScope, componentScope) {
    if (componentScope.props.wallmode) {
      return chartUtils.tvTooltipLead(`${chartScope.y} work item${chartScope.y != 1 ? 's' : ''}`, moment(chartScope.x).format(componentScope.props.dateFormat));
    }
    let tooltipHtml = '';
    const s = [];
    tooltipHtml += `<div class="tooltip-label-item">${moment(
      chartScope.x
    ).format(componentScope.props.dateFormat)}</div>`;
    tooltipHtml += `<div class="tooltip-label-item">${
      chartScope.y
    } work item${chartScope.y != 1 ? 's' : ''}</div>`;
    s.push(tooltipHtml);
    return s;
  }

  render() {
    const isPieChart = this.props.wipBreakdownType === 'pie';
    const noSlices = isPieChart && !this.props.wipBreakdownLoading && !!this.props.wipBreakdownData && !this.props.wipBreakdownData.length;

    return (
      <Draggable
        defaultPosition={{ x: 95, y: 112 }}
        position={this.state.pos}
        onStop={this.handleDrag}
        axis="both"
        bounds="parent"
      >
        <div
          className={`box_white_shadow chart_averages_box ${isPieChart ? 'breakdown' : ''} ${this.state.isFullScreen ? 'fullscreen' : ''}`}
          style={{ width: this.state.width, height: this.state.height }}
        >
          <div className="box_white_title mb-4">
            <h3>{isPieChart && !this.state.isListView ? 'WIP breakdown' : 'WIP'}</h3>
            {/* the card's number on its title's line, so the chart keeps the card's height */}
            <p className="card-title-value">
              {this.state.currentWip}
              {' '}
              work item
              {this.state.currentWip != 1 ? 's' : ''}
            </p>
            <div className="right-actions">
              {/* the card's three views in one control, like the Breakdown card's: the list, the pie and the line */}
              <ViewSwitch
                views={[{ id: 'list', icon: legendIcon }, { id: 'pie', icon: pieIcon }, { id: 'line', icon: lineChartIcon }]}
                active={this.getView()}
                onChange={this.handleView}
              />
              <Button
                variant="secondary"
                size="sm"
                className={`btn-action ${this.state.isFullScreen ? 'fullscreen' : ''}`}
                onClick={this.handleResize}
              >
                <img src={this.state.isFullScreen ? expandIcon : collapseIcon} alt="toggleFullScreen" />
              </Button>
              {/* in focus the chart can be downloaded, from the header like the card's other actions */}
              {this.state.isFullScreen && <ChartDownloadMenu getChart={() => this.chart} />}
              <Button className="btn-action" onClick={this.handleClose}>
                &times;
              </Button>
            </div>
          </div>
          <div id="daily_wip_chart_container" />
          {(this.state.isListView || noSlices) && this.renderRankedList()}
          {!this.state.isListView && isPieChart && !this.state.isFullScreen && (
            <PieLegend
              offset={this.state.pieNamesOffset}
              data={this.props.wipBreakdownData || []}
              hovered={this.state.hoveredSlice}
              hidden={this.state.hiddenSlices}
              onHover={this.highlightSlice}
              onToggle={this.toggleSlice}
            />
          )}
        </div>
      </Draggable>
    );
  }
}

const mapDispatchToProps = dispatch => {
  return {
    close: () => dispatch(updateLineChart(false, 'cta')),
    saveConfig: () => dispatch(saveConfig()),
    setWipBreakdownType: type => dispatch(updateWipBreakdownType(type, 'cta')),
    getWipBreakdownData: (data, day, breakdownBy) => dispatch(getWipBreakdownData(data, day, breakdownBy))
  };
};

const mapStateToProps = state => {
  return {
    wallmode: state.app.wallmode,
    dateFormat: state.filters.dateFormat,
    dashboard: state.dashboards.selected,
    wipBreakdownType: state.filters.cta.wipBreakdownType || 'line',
    wipBreakdownBy: state.filters.cta.wipBreakdownBy || 'lists',
    wipBreakdownData: state.cta.wipBreakdown,
    wipBreakdownLoading: state.cta.wipBreakdownLoading
  };
};

export default withChartExport(connect(mapStateToProps, mapDispatchToProps)(DailyWIP));
